import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../lib/api.js";
import {
  createIncomeEvent,
  updateIncomeEvent,
} from "../../lib/api-modules/income-events.js";
import {
  INCOME_EVENT_TYPE_LABELS,
  type CreateIncomeEventInput,
  type EquityGrant,
  type IncomeEvent,
  type IncomeEventType,
  type UpdateIncomeEventInput,
} from "../../lib/api-modules/types.js";
import { centsToDollars, dollarsToCents } from "../../lib/util/money.js";
import { ErrorMessage } from "../auth/error-message.js";

interface IncomeEventFormProps {
  readonly mode: "create" | "edit";
  readonly event?: IncomeEvent;
  readonly equityGrants?: ReadonlyArray<EquityGrant>;
  readonly onClose: () => void;
}

const EVENT_TYPE_OPTIONS = (
  Object.keys(INCOME_EVENT_TYPE_LABELS) as IncomeEventType[]
).map((v) => ({ value: v, label: INCOME_EVENT_TYPE_LABELS[v] }));

export function IncomeEventForm({
  mode,
  event,
  equityGrants,
  onClose,
}: IncomeEventFormProps) {
  const queryClient = useQueryClient();
  const isEdit = mode === "edit";
  const today = new Date().toISOString().slice(0, 10);

  const [date, setDate] = useState<string>(event?.date ?? today);
  const [eventType, setEventType] = useState<IncomeEventType>(
    event?.event_type ?? "salary_paycheck",
  );
  const [payer, setPayer] = useState<string>(event?.payer ?? "");
  const [amountDollars, setAmountDollars] = useState<string>(
    event ? centsToDollars(event.amount_cents) : "",
  );
  const [grossDollars, setGrossDollars] = useState<string>(
    event?.gross_cents !== undefined ? centsToDollars(event.gross_cents) : "",
  );
  const [netDollars, setNetDollars] = useState<string>(
    event?.net_cents !== undefined ? centsToDollars(event.net_cents) : "",
  );
  const [withholdingDollars, setWithholdingDollars] = useState<string>(
    event?.withholding_cents !== undefined
      ? centsToDollars(event.withholding_cents)
      : "",
  );
  const [equityGrantId, setEquityGrantId] = useState<string>(
    event?.equity_grant_id ?? "",
  );
  const [notes, setNotes] = useState<string>(event?.notes ?? "");

  const create = useMutation({
    mutationFn: (input: CreateIncomeEventInput) => createIncomeEvent(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["income-events"] });
      onClose();
    },
  });
  const update = useMutation({
    mutationFn: (input: UpdateIncomeEventInput) =>
      updateIncomeEvent(event!.date, event!.event_id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["income-events"] });
      onClose();
    },
  });
  const m = isEdit ? update : create;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const base = {
      amount_cents: dollarsToCents(amountDollars),
      event_type: eventType,
      payer: payer.trim(),
      ...(grossDollars
        ? { gross_cents: dollarsToCents(grossDollars) }
        : {}),
      ...(netDollars ? { net_cents: dollarsToCents(netDollars) } : {}),
      ...(withholdingDollars
        ? { withholding_cents: dollarsToCents(withholdingDollars) }
        : {}),
      ...(equityGrantId ? { equity_grant_id: equityGrantId } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    };
    if (isEdit) {
      update.mutate(base);
    } else {
      create.mutate({ ...base, date });
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-md border border-zinc-300 bg-zinc-50 p-4 dark:border-zinc-700 dark:bg-zinc-900"
    >
      <h3 className="mb-3 text-sm font-semibold">
        {isEdit ? "Edit income event" : "Add income event"}
      </h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs">
          Date
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={isEdit}
            required
            className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm disabled:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-950"
          />
          {isEdit && (
            <span className="text-[10px] text-zinc-500">
              Date not editable — delete + re-create to change.
            </span>
          )}
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Type
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value as IncomeEventType)}
            className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-950"
          >
            {EVENT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Payer
          <input
            value={payer}
            onChange={(e) => setPayer(e.target.value)}
            required
            placeholder="e.g. Acme Corp"
            className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-950"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Amount (USD)
          <input
            type="number"
            step="0.01"
            value={amountDollars}
            onChange={(e) => setAmountDollars(e.target.value)}
            required
            className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-950"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Gross (optional)
          <input
            type="number"
            step="0.01"
            value={grossDollars}
            onChange={(e) => setGrossDollars(e.target.value)}
            className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-950"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Net (optional)
          <input
            type="number"
            step="0.01"
            value={netDollars}
            onChange={(e) => setNetDollars(e.target.value)}
            className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-950"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Withholding (optional)
          <input
            type="number"
            step="0.01"
            value={withholdingDollars}
            onChange={(e) => setWithholdingDollars(e.target.value)}
            className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-950"
          />
        </label>
        {equityGrants && equityGrants.length > 0 && (
          <label className="flex flex-col gap-1 text-xs">
            Equity grant (optional)
            <select
              value={equityGrantId}
              onChange={(e) => setEquityGrantId(e.target.value)}
              className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-950"
            >
              <option value="">—</option>
              {equityGrants.map((g) => (
                <option key={g.grant_id} value={g.grant_id}>
                  {g.ticker} · {g.grant_type} · {g.grant_date}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <label className="mt-3 flex flex-col gap-1 text-xs">
        Notes
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-950"
        />
      </label>
      {m.error && (
        <ErrorMessage>
          {m.error instanceof ApiError ? m.error.message : "Couldn't save"}
        </ErrorMessage>
      )}
      <div className="mt-3 flex gap-2">
        <button
          type="submit"
          disabled={m.isPending}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:opacity-60"
        >
          {m.isPending ? "Saving…" : isEdit ? "Save" : "Add"}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-zinc-300 px-3 py-1.5 text-xs hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
